import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectEmailSendingFromState,
  selectEmailSentFromState,
  selectTopLevelErrorFromState,
} from 'src/app/ResetPassword/redux/resetPassword.selectors'
import Loading from 'src/assets/images/Loading.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { signinUrl } from 'src/navigation/urls'
import { postResetPassword } from 'src/redux/actions/auth.actions'
import {
  selectEmailFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const Auth0ForgotPassword: React.FC = () => {
  const dispatch = useBackgroundDispatch()

  const email = useSelector(selectEmailFromState)
  const emailSent = useSelector(selectEmailSentFromState)
  const emailSending = useSelector(selectEmailSendingFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)

  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  useEffect((): void => {
    if (emailSent === true) {
      setShowSuccessMessage(true)
    }
  }, [emailSent])

  return (
    <>
      {!showSuccessMessage ? (
        <img
          css={css`
            height: 120px;
            width: 90px;
          `}
          alt={translateSingleString(locale, 'Loading')}
          src={Loading}
        />
      ) : (
        <span
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 40px;
            `}
          >
            <Trns source={'Forgot Password'} />
          </span>
          <p
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 30px;
            `}
          >
            {showSuccessMessage && (
              <Trns
                source={
                  "We've emailed password update instructions to ${email}."
                }
                expressions={{
                  email: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {email}
                    </span>
                  ),
                }}
              />
            )}
          </p>
          <p
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {showSuccessMessage && (
              <Trns
                source={`If a profile exists with that email, you should receive it shortly. If you can't find the email, be sure to check your spam folder, otherwise resend the instructions.`}
              />
            )}
          </p>
          {topLevelError && (
            <span
              css={css`
                margin-top: 30px;
                align-self: center;
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {topLevelError}
            </span>
          )}
          <Button
            type={ButtonTypes.submit}
            css={css`
              margin-top: 40px;
            `}
            design={ButtonDesigns.designCta}
            text={<Trns source={'Resend Instructions'} />}
            loading={emailSending}
            disabled={emailSending}
            onClick={(): void => {
              dispatch(postResetPassword(email))
            }}
            big
          />
          <HoverableLink
            to={signinUrl()}
            css={css`
              margin-top: 30px;
              align-self: center;
            `}
            underline
            ariaLabelSource={'Sign in'}
          >
            <Trns source={'Back to Login'} />
          </HoverableLink>
        </span>
      )}
    </>
  )
}

export default Auth0ForgotPassword
